/* eslint-disable */
import _ from 'lodash';

export function getSchemaTypeForPath(path, schema) {
  path = path.replace(/\[(\d+)\]/g, '.$1'); // Convert array access to properties (LLMs might decide to write code with arrays)
  const pathParts = path.split('.');
  let current = schema.properties;
  for (let i = 0; i < pathParts.length; i++) {
    const part = pathParts[i];
    if (!current || !current[part]) {
      return null;
    }

    current = current[part];
    const currentType = current.type;
    if (currentType === 'object') {
      current = current.properties;
      if (i === pathParts.length - 1) {
        // Hack to return the correct type for objects, in case the object has a property called "type" (it happens with mechanical.steering)
        current = { ...current, type: 'object'};
      }
    } else if (currentType === 'array') {
      const nextKey = pathParts[i + 1];
      if (nextKey?.match(/^\d+$/)) {
        current = { [nextKey]: current.items };
      }
    } else if (currentType === 'objectTypedKeys') {
      const nextKey = pathParts[i + 1];
      if (current.keys.includes(nextKey)) {
        current = { [nextKey]: current.valuesType };
      }
    } else if (currentType === 'unit') {
      current = { value: { type: 'number' }, unit: { type: 'unitRegex', unit: current.unit }, type: 'unit' };
    }
  }

  return current;
}

export function pathExistsInSchema(path, schema) {
  return !!getSchemaTypeForPath(path, schema);
}

export function serializeSchemaForLLM(schema, removeHiddenFields = true) {
  // Remove _hideToLLM and _required fields from the schema
  const removeHiddenAndRequired = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      if (key === '_required') {
        continue;
      }
      if (removeHiddenFields && key.startsWith('_') && key !== '_comments') {
        continue;
      }

      if (_.isPlainObject(value)) {
        if (!value.properties?._hideToLLM) {
          result[key] = removeHiddenAndRequired(value);
        }
      } else if (_.isArray(value)) {
        result[key] = removeHiddenAndRequired(_.reject(value, v => removeHiddenFields && _.isString(v) && v.startsWith('_')));
      } else {
        result[key] = value;
      }
    }
    return result;
  };

  return JSON.stringify(removeHiddenAndRequired(schema.properties), null, 2);
}

export function expandObjectKeys(specs, prefix = '') {
  if (_.isArray(specs) && _.isArray(specs[0])) { // if coming from factoid.derivedSpecs, they will be an array of pairs
    specs = _.fromPairs(specs);
  }
  return _.reduce(specs, (res, val, key) => {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (_.isPlainObject(val) || _.isArray(val)) {
      _.assign(res, expandObjectKeys(val, fullKey));
    } else {
      res[fullKey] = val;
    }
    return res;
  }, {});
}

export function unifySpecs(specs, schema) {
  const mergedSpecs = [];
  const specPathToFragmentIds = [];
  const warnings = [];
  const arrayPaths = new Set();

  if (specs?.length) {
    for (const json of specs) {
      const pathToFragmentIds = {};
      const specWarnings = [];
      const merged = {};
      for (let fragment of json) {
        for (let factoid of fragment.factoids) {
          if(!_.isArray(factoid.derivedSpecs)) {
            continue;
          }

          let factoidArrayOffsets = {};

          for (let [path, value] of factoid.derivedSpecs) {
            let existing = _.get(merged, path);

            let pathParts = path.split('.');

            // Try to detect array index positions and concatenate items
            let arrayIndex = _.findIndex(pathParts, p => p.match(/^\d+$/));
            let arrayPath = pathParts.slice(0, arrayIndex).join('.');
            const pathType = getSchemaTypeForPath(arrayPath, schema)?.type;

            if(arrayIndex >= 0 && pathType === 'array') {
              arrayPaths.add(arrayPath);

              let restPath = pathParts.slice(arrayIndex+1).join('.');

              let array = _.get(merged, arrayPath);
              let arrayOffset = factoidArrayOffsets[arrayPath];

              if(!array) {
                array = [];
                arrayOffset = 0;
                factoidArrayOffsets[arrayPath] = arrayOffset;
              } else if(arrayOffset === undefined) {
                arrayOffset = 1 + (_.max(_.keys(array).filter(k => k.match(/^\d+$/)).map(k => parseInt(k))) ?? -1);
                factoidArrayOffsets[arrayPath] = arrayOffset;
              }

              _.set(merged, arrayPath, array);

              path = `${arrayPath}.${arrayOffset + parseInt(pathParts[arrayIndex])}${restPath ? '.' + restPath : ''}`;
              existing = _.get(merged, path);
            }

            if (existing !== undefined) {
              if (!_.isEqual(existing, value)) {
                specWarnings.push({ path, type: 'contradiction', value });
              } else {
                specWarnings.push({ path, type: 'duplicate', });
              }
            } else {
              // Always create objects (this is a problem with gearRatios, where sometimes it creates an array)
              _.setWith(merged, path, value, Object);
            }

            if(factoid.missingInSchema?.length) {
              specWarnings.push({ path, type: 'missingSpec', value: factoid.missingInSchema });
            }

            if(fragment.factoids.length > 1) {
              // Check if this path is in all factoids, if not, add a new notice
              if(!_.every(fragment.factoids, f => f.isObsolete || _.some(f.derivedSpecs, p => p[0] === path))) {
                specWarnings.push({ path, type: 'noConsensus' });
              }
            }

            (pathToFragmentIds[path] = (pathToFragmentIds[path] || [])).push(fragment._id);
          }
        }
      }

      mergedSpecs.push(sortObjectKeysDeep(merged));
      warnings.push(specWarnings);
      specPathToFragmentIds.push(pathToFragmentIds);
    }
  }

  // For each array path, find unique items, and recompute each array matching the unique items
  for(const arrayPath of arrayPaths) {
    let arrays = mergedSpecs.map(json => _.values(_.get(json, arrayPath)));
    const mergedArray = _.compact(_.uniqWith(_.flatten(arrays), _.isEqual));

    _.each(arrays, (originalArray, i) => {
      let toRemapSpecPathToFragmentIds = {};
      let toDeleteSpecPathToFragmentIds = {};

      let array = _.map(mergedArray, (v,j) => {
        const matchingIndex = _.findIndex(originalArray, o => _.isEqual(o, v));
        if(matchingIndex >= 0) {
          // Fix specPathToFragmentIds so that we have a valid reference to the fragment that generated the id
          const oldPath = `${arrayPath}.${matchingIndex}`;
          const newPath = `${arrayPath}.${j}`;
          _.forEach(specPathToFragmentIds[i], (value, path) => {
            if(path.startsWith(oldPath)) {
              // Save which paths should be remapped. Don't do it now because new path might be the old path for other spec
              toRemapSpecPathToFragmentIds[path.replace(oldPath, newPath)] = value;
              toDeleteSpecPathToFragmentIds[path] = true;
            }
          });

          return v;
        } else {
          return undefined;
        }
      });

      // Apply changes to specPathToFragmentIds
      _.each(toDeleteSpecPathToFragmentIds, (_, path) => delete specPathToFragmentIds[i][path]);
      _.each(toRemapSpecPathToFragmentIds, (value, path) => specPathToFragmentIds[i][path] = value);

      _.set(mergedSpecs[i], arrayPath, array);
    });
  }

  return { mergedSpecs, warnings, specPathToFragmentIds, arrayPaths };
}

export function sortObjectKeysDeep(object) {
  if (_.isPlainObject(object)) {
    const result = {};
    _.forEach(_.sortBy(Object.keys(object), k => k.toLowerCase()), key => {
      result[key] = sortObjectKeysDeep(object[key]);
    });
    return result;
  } else {
    return object;
  }
}

export function validateSpecsAccordingToSchema(specs, schema) {
  const errors = [];
  if (!specs) {
    return errors;
  }

  for (const [path, value] of _.toPairs(specs)) {
    const schemaType = getSchemaTypeForPath(path, schema);
    if (!schemaType) {
      errors.push({ type: 'invalidPath', path, value: path });
      continue;
    }

    const invalidSpecError = { type: 'invalidValue', path, value };
    const valueExpectedType = schemaType.type;
    if (_.includes(['boolean', 'number', 'string'], valueExpectedType)) {
      if (typeof value !== valueExpectedType) {
        errors.push(invalidSpecError);
      }
    } else if (valueExpectedType === 'enum') {
      if (!_.includes(schemaType.values, value)) {
        errors.push(invalidSpecError);
      }
    } else if (valueExpectedType === 'unitRegex') {
      if (!value.match(`^(${schemaType.unit.split(/\s*\|\s*/).join('|')})\$`)) { // Create a usable regex removing all whitespace
        errors.push(invalidSpecError);
      }
    } else {
      errors.push(invalidSpecError);
    }
  }

  return errors;
}

export function validateRequiredAccordingToSchema(specs, schema) {
  const errors = [];

  function checkConditionalRequire(matchOptions, {currentFullPath}) {
    // For objectKeyTypedKeys, we need to return a conditional require value for the required keys
    /** @type {boolean | Record<string, boolean>} */
    let trueResult = true;
    for(let [path, condition] of _.toPairs(matchOptions)) {
      if(path === '$currentFullPath') {
        path = currentFullPath;
      }
      let value = _.get(specs, path);
      if (_.isObject(condition)) {
        let { $ne, $in, $nin, $exists, $eq, $hasKeys, $isDescendantOf } = condition;

        if ($ne !== undefined && _.isEqual(value, $ne)) {
          return false;
        } else if ($in !== undefined && !_.includes($in, value)) {
          return false;
        } else if ($nin !== undefined && _.includes($nin, value)) {
          return false;
        } else if ($exists !== undefined && ($exists ? !value : value)) {
          return false;
        } else if ($hasKeys !== undefined) {
          let missingKeys = _.difference($hasKeys, Object.keys(value || {}));
          for(const key of missingKeys) {
            errors.push({ type: 'requiredMissing', path: `${path}.${key}` });
          }
          // Return a conditional require value only for the required keys
          trueResult = _.fromPairs(_.toPairs($hasKeys).map((key) => [key, true]));
        } else if ($isDescendantOf !== undefined) {
          if(_.some($isDescendantOf, parent => currentFullPath.startsWith(parent))) {
            continue;
          }
          // No parent matched condition
          return false;
        } else if ($eq !== undefined && !_.isEqual(value, $eq)) {
          return false;
        }
      } else if(!_.isEqual(value, condition)) {
        return false;
      }
    }
    return trueResult;
  }
  // Iterate the schema and check if all required properties are present in the specs

  const findRequiredMissing = (path, inheritedRequired, subschema) => {
    let { type, properties, valuesType, items, _required } = subschema;
    type = type || 'object';

    let effectiveRequired = _required ?? inheritedRequired;

    const value = path ? _.get(specs, path) : specs;
    const parentPath = path ? path + '.' : '';

    if(_.isObject(effectiveRequired)) {
      // This can return both a boolean or an object with the required value for each children keys/subproperties
      effectiveRequired = checkConditionalRequire(effectiveRequired, {currentFullPath: path});
    }

    if (value == undefined && effectiveRequired) {
      errors.push({ type: 'requiredMissing', path });
    } else {
      switch (type) {
        case 'object':
          for (const subprop of Object.keys(properties)) {
            let subpropRequired = _.isObject(effectiveRequired) ? (effectiveRequired[subprop] ?? false) : effectiveRequired;
            findRequiredMissing(`${parentPath}${subprop}`, subpropRequired, properties[subprop]);
          }
          break;
        case 'array':
          for (let i = 0; i < (value || []).length; i++) {
            let subitemRequired = _.isObject(effectiveRequired) ? (effectiveRequired[i] ?? false) : effectiveRequired;
            findRequiredMissing(`${parentPath}${i}`, subitemRequired, items);
          }
          break;
        case 'objectTypedKeys':
          if (value) {
            for (const key of Object.keys(value)) {
              let subkjeyRequired = _.isObject(effectiveRequired) ? (effectiveRequired[key] ?? false) : effectiveRequired;
              findRequiredMissing(`${parentPath}${key}`, subkjeyRequired, valuesType);
            }
          }
          break;
        case 'unit':
        default:
        // Stop recursion
      }
    }
  };

  findRequiredMissing(null, false, schema);

  return errors;
}

export function getSchemaRegex(maxRevisionVersion) {
  if (!maxRevisionVersion || !maxRevisionVersion.match(/^\d{3}$/)) {
    return undefined;
  }

  let [d3, d2, d1] = maxRevisionVersion.split('').map(n => Number.parseInt(n));
  let a = d2 > 0 ? `${d3}[0-${d2-1}]\\d` : null;
  let b = d1 > 0 ? `${d3}${d2}[0-${d1-1}]` : null;
  let c = d3 > 0 ? `[0-${d3 - 1}]\\d\\d` : null;
  return `^\\d+\\.\\d+\\.(${_.compact([a, b, c]).join('|')})$`;
}
